import React, { useState, useCallback } from 'react';
import { Molecule } from '../../types/chemistry';

interface SelectionActionBarProps {
  selectedMolecules: Molecule[];
  totalMolecules: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onRegister: () => void;
  onCompare: () => void;
  onStandardise?: () => void;
  onEnrich?: () => void;
  onExport?: () => void;
  onDeduplicate?: () => void;
  onDelete?: () => void;
  onSubmitForAssay?: () => void;
  onQuickSearch?: () => void;
  /** Hide chemistry actions (Register etc.) for non-chemistry files */
  hideChemistryActions?: boolean;
}

const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  selectedMolecules,
  totalMolecules,
  onSelectAll,
  onClearSelection,
  onRegister,
  onCompare,
  onStandardise,
  onEnrich,
  onExport,
  onDeduplicate,
  onDelete,
  onSubmitForAssay,
  onQuickSearch,
  hideChemistryActions = false,
}) => {
  const [copiedToast, setCopiedToast] = useState(false);
  const isVisible = selectedMolecules.length > 0;

  const handleCopySmiles = useCallback(() => {
    const smilesText = selectedMolecules
      .map(m => m.smiles)
      .filter(Boolean)
      .join('\n');
    navigator.clipboard.writeText(smilesText).then(() => {
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 2000);
    });
  }, [selectedMolecules]);

  return (
    <div
      className="selection-action-bar"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 56,
        zIndex: 30,
        background: '#1a1f2e',
        borderTop: '2px solid #0D9488',
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        fontFamily: 'Inter, system-ui, sans-serif',
        transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 200ms ease',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      {/* LEFT: Selection info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>
          {Math.min(selectedMolecules.length, totalMolecules)} of {totalMolecules.toLocaleString()} selected
        </span>
        <button
          onClick={onSelectAll}
          style={{
            background: 'none',
            border: 'none',
            color: '#0D9488',
            fontSize: 12,
            cursor: 'pointer',
            padding: '2px 4px',
            fontFamily: 'inherit',
          }}
        >
          Select All
        </button>
        <span style={{ color: '#555', fontSize: 12 }}>|</span>
        <button
          onClick={onClearSelection}
          style={{
            background: 'none',
            border: 'none',
            color: '#9CA3AF',
            fontSize: 12,
            cursor: 'pointer',
            padding: '2px 4px',
            fontFamily: 'inherit',
          }}
        >
          Clear
        </button>
      </div>

      {/* RIGHT: Action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {/* Group 1: Chemistry */}
        {!hideChemistryActions && (
          <>
            <ActionButton
              icon={<ClipboardIcon />}
              label="Register"
              onClick={onRegister}
              tooltip={`Register ${selectedMolecules.length} selected compound${selectedMolecules.length !== 1 ? 's' : ''} to the corporate registry`}
            />
            <ActionButton
              icon={<FlaskIcon />}
              label="Standardise"
              onClick={onStandardise}
              tooltip="Standardise selected structures — normalise salts, charges, and tautomers"
            />
            <ActionButton
              icon={<BeakerIcon />}
              label="Assay"
              onClick={onSubmitForAssay}
              tooltip={`Submit ${selectedMolecules.length} compound${selectedMolecules.length !== 1 ? 's' : ''} for biological assay`}
            />
            <ActionButton
              icon={<LayersIcon />}
              label="De-duplicate"
              onClick={onDeduplicate}
              tooltip="Find and merge duplicate structures in the selection"
            />
            <Divider />
          </>
        )}

        {/* Group 2: Analysis */}
        {selectedMolecules.length === 1 && <ActionButton
          icon={<SearchIcon />}
          label="Quick Search"
          onClick={onQuickSearch}
          tooltip="Open everything known about this compound in Quick Search"
        />}
        <ActionButton
          icon={<CompareIcon />}
          label="Compare"
          onClick={onCompare}
          tooltip={`Compare ${selectedMolecules.length} compounds — open sortable property table`}
        />
        <ActionButton
          icon={<SparklesIcon />}
          label="Enrich"
          onClick={onEnrich}
          tooltip="Run enrichment on selected compounds — targets, pathways, and patent data"
        />
        <Divider />

        {/* Group 3: Data */}
        <ActionButton
          icon={<DownloadIcon />}
          label="Export"
          onClick={onExport}
          tooltip="Export selected compounds as SDF, CSV, or Excel"
        />
        <div style={{ position: 'relative' }}>
          <ActionButton
            icon={<CopyIcon />}
            label={copiedToast ? 'Copied!' : 'Copy SMILES'}
            onClick={handleCopySmiles}
            tooltip="Copy SMILES strings to clipboard, one per line"
          />
        </div>
        <ActionButton
          icon={<TrashIcon />}
          label="Delete"
          onClick={onDelete}
          danger
          tooltip="Permanently delete selected compounds from this file"
        />
      </div>
    </div>
  );
};

/* ── Action Button ──────────────────────────────────────────────── */

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
  danger?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  label,
  onClick,
  disabled = false,
  tooltip,
  danger = false,
}) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    title={tooltip}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      padding: '6px 10px',
      borderRadius: 6,
      border: 'none',
      background: 'transparent',
      color: disabled ? 'rgba(255,255,255,0.4)' : '#E5E7EB',
      fontSize: 12,
      fontWeight: 500,
      fontFamily: 'Inter, system-ui, sans-serif',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 150ms, color 150ms',
      whiteSpace: 'nowrap',
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.background = 'rgba(13,148,136,0.2)';
        e.currentTarget.style.color = danger ? '#F87171' : '#0D9488';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = disabled ? 'rgba(255,255,255,0.4)' : '#E5E7EB';
    }}
  >
    {icon}
    {label}
  </button>
);

/* ── Divider ────────────────────────────────────────────────────── */

const Divider: React.FC = () => (
  <div
    style={{
      width: 1,
      height: 24,
      background: 'rgba(255,255,255,0.12)',
      margin: '0 6px',
    }}
  />
);

/* ── SVG Icons (14×14) ──────────────────────────────────────────── */

const iconProps = {
  width: 14,
  height: 14,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  viewBox: '0 0 24 24',
};

const SearchIcon = () => <svg {...iconProps} viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>;

const ClipboardIcon = () => (
  <svg {...iconProps}>
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const FlaskIcon = () => (
  <svg {...iconProps}>
    <path d="M9 3h6M10 3v6.5L4 20h16L14 9.5V3" />
  </svg>
);

const BeakerIcon = () => (
  <svg {...iconProps}>
    <path d="M8 3h8M9 3v5.172a2 2 0 01-.586 1.414l-5 5c-1.26 1.26-.367 3.414 1.415 3.414h14.342c1.782 0 2.674-2.154 1.414-3.414l-5-5A2 2 0 0115 8.172V3" />
  </svg>
);

const LayersIcon = () => (
  <svg {...iconProps}>
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);

const CompareIcon = () => (
  <svg {...iconProps}>
    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SparklesIcon = () => (
  <svg {...iconProps}>
    <path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const DownloadIcon = () => (
  <svg {...iconProps}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

const CopyIcon = () => (
  <svg {...iconProps}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const TrashIcon = () => (
  <svg {...iconProps}>
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

export default SelectionActionBar;
